import { TransportError } from "@econ-v1/domain";
import { decodeClientRpcFrame, encodeClientRpcFrame, CLIENT_RPC_VERSION } from "../client-rpc/frame.js";
import { clientRpcResponseError } from "../client-rpc/response-error.js";
import { DataChannelFrameReassembler, MAX_DATACHANNEL_CHUNK_BYTES, segmentFrame } from "./datachannel-segmentation.js";
/**
 * Browser adapter for the WebRTC DataChannel carrier (ticket #1825, "N4").
 *
 * Owns one `RTCPeerConnection` and one reliable, ordered RPC DataChannel it
 * creates itself (the browser is always the offerer against this crate's
 * Rust `WebRtcClientRpcTransport`, which only ever accepts offers).
 * Depends on the narrow `WebRtcSignalingPort` (N3's authenticated
 * HTTPS/WSS signaling adapter today) rather than directly on any signaling
 * transport, so the signaling channel can change without touching WebRTC
 * connection logic.
 *
 * `HomeNodeClient.invoke()` has no per-call `AbortSignal`, so cancellation
 * here is scoped to what the shared `node/client-rpc/1` protocol actually
 * supports: the one in-flight request is cancelled (a `Cancel` frame is
 * sent) if `close()` runs while it is outstanding.
 */
const DATA_CHANNEL_LABEL = "rpc";
/** Below this many buffered bytes, resume flushing the outbound queue. */
const BUFFERED_AMOUNT_LOW_THRESHOLD = MAX_DATACHANNEL_CHUNK_BYTES * 4;
/**
 * Browser client for the transport-neutral client-rpc envelope over a
 * WebRTC DataChannel. Implements `HomeNodeClient` beside
 * `HttpWsHomeNodeClient` and `IrohHomeNodeClient`.
 */
export class WebRtcHomeNodeClient {
    #capabilityForOperation;
    #iceServers;
    #idGenerator;
    #listeners = new Set();
    #now;
    #offerNonceGenerator;
    #peerConnectionFactory;
    #setupHeaders;
    #signaling;
    #inboundReassembler = new DataChannelFrameReassembler();
    #outbound = [];
    #channel;
    #closed = false;
    #connectPromise;
    #offerNonce;
    #pending;
    #pendingRequestId;
    #peerConnection;
    /** Serializes `invoke()` calls: the shared server session allows only one in-flight request at a time. */
    #requestQueue = Promise.resolve();
    state;
    constructor(options) {
        this.#capabilityForOperation = options.capabilityForOperation ?? ((operation) => operation);
        this.#iceServers = options.iceServers ?? [];
        this.#idGenerator = options.idGenerator ?? (() => `client-rpc-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        this.#now = options.now ?? (() => new Date().toISOString());
        this.#offerNonceGenerator = options.offerNonceGenerator ?? defaultOfferNonceGenerator;
        this.#peerConnectionFactory = options.peerConnectionFactory ?? defaultPeerConnectionFactory;
        this.#setupHeaders = options.setupHeaders ?? (async () => ({}));
        this.#signaling = options.signaling;
        this.state = { changedAt: this.#now(), path: "offline", status: "offline" };
    }
    /** Establishes the peer connection, DataChannel, and signaling session. Idempotent while in flight. */
    async connect() {
        if (this.#closed)
            throw closedError();
        this.#connectPromise ??= this.#connectOnce().catch((error) => {
            this.#connectPromise = undefined;
            throw error;
        });
        return this.#connectPromise;
    }
    async invoke(request) {
        if (this.#closed)
            throw closedError();
        const run = this.#requestQueue.then(async () => await this.#invokeOnce(request));
        this.#requestQueue = run.then(() => undefined, () => undefined);
        return await run;
    }
    async *deltas(request) {
        const response = await this.invoke({
            operation: "client-sync.deltas",
            payload: { cursor: request.cursor, domain: request.domain },
        });
        if (!Array.isArray(response))
            throw new TransportError({ code: "unavailable", message: "WebRTC delta response was malformed" });
        for (const delta of response)
            yield delta;
    }
    /** Explicit ICE restart under a fresh offer nonce, superseding the current one. */
    async restartIce() {
        if (this.#closed || this.#peerConnection === undefined || this.#offerNonce === undefined)
            return;
        const previousOfferNonce = this.#offerNonce;
        const nextOfferNonce = this.#offerNonceGenerator();
        this.#peerConnection.restartIce();
        const offer = await this.#peerConnection.createOffer();
        await this.#peerConnection.setLocalDescription(offer);
        const sdp = this.#peerConnection.localDescription?.sdp;
        if (sdp === undefined)
            throw new TransportError({ code: "unavailable", message: "WebRTC restart produced no local SDP" });
        const fingerprint = extractDtlsFingerprint(sdp);
        if (fingerprint === undefined)
            throw new TransportError({ code: "unavailable", message: "WebRTC restart SDP has no DTLS fingerprint" });
        try {
            const answer = await this.#signaling.sendRestart({
                dtls_fingerprint: fingerprint,
                offer_nonce: nextOfferNonce,
                previous_offer_nonce: previousOfferNonce,
                sdp,
            });
            await this.#peerConnection.setRemoteDescription({ sdp: answer.sdp, type: "answer" });
            this.#offerNonce = nextOfferNonce;
        }
        catch {
            this.#setState({ changedAt: this.#now(), path: "offline", status: "offline" });
            throw new TransportError({ code: "offline", message: "WebRTC ICE restart failed" });
        }
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }
    close() {
        if (this.#closed)
            return;
        this.#closed = true;
        if (this.#pending !== undefined && this.#pendingRequestId !== undefined) {
            this.#sendCancelBestEffort(this.#pendingRequestId);
            this.#pending.reject(closedError());
            this.#pending = undefined;
            this.#pendingRequestId = undefined;
        }
        void this.#signaling.close(this.#offerNonce === undefined ? {} : { offer_nonce: this.#offerNonce }).catch(() => undefined);
        this.#channel?.close();
        this.#peerConnection?.close();
        this.#channel = undefined;
        this.#peerConnection = undefined;
        this.#setState({ changedAt: this.#now(), path: "offline", status: "offline" });
        this.#listeners.clear();
    }
    async #connectOnce() {
        const peerConnection = this.#peerConnectionFactory({ iceServers: this.#iceServers });
        this.#peerConnection = peerConnection;
        const channel = peerConnection.createDataChannel(DATA_CHANNEL_LABEL, { ordered: true });
        this.#channel = channel;
        channel.bufferedAmountLowThreshold = BUFFERED_AMOUNT_LOW_THRESHOLD;
        channel.addEventListener("bufferedamountlow", () => this.#flushOutbound());
        channel.addEventListener("message", (event) => this.#onChannelMessage(event));
        channel.addEventListener("close", () => this.#onChannelClosed());
        channel.addEventListener("error", () => this.#onChannelClosed());
        peerConnection.addEventListener("icecandidate", (event) => this.#onLocalCandidate(event));
        peerConnection.addEventListener("iceconnectionstatechange", () => this.#onIceConnectionStateChange());
        await this.#signaling.connect(await this.#setupHeaders());
        const offerNonce = this.#offerNonceGenerator();
        this.#offerNonce = offerNonce;
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        const sdp = peerConnection.localDescription?.sdp;
        if (sdp === undefined)
            throw new TransportError({ code: "unavailable", message: "WebRTC offer produced no local SDP" });
        const fingerprint = extractDtlsFingerprint(sdp);
        if (fingerprint === undefined)
            throw new TransportError({ code: "unavailable", message: "WebRTC offer SDP has no DTLS fingerprint" });
        const answer = await this.#signaling.sendOffer({ dtls_fingerprint: fingerprint, offer_nonce: offerNonce, sdp });
        await peerConnection.setRemoteDescription({ sdp: answer.sdp, type: "answer" });
        await this.#waitForChannelOpen(channel);
        this.#setState({ changedAt: this.#now(), path: "webrtc-direct", status: "connected" });
    }
    async #waitForChannelOpen(channel) {
        if (channel.readyState === "open")
            return;
        await new Promise((resolve, reject) => {
            const onOpen = () => resolve();
            const onFailure = () => reject(new TransportError({ code: "offline", message: "WebRTC data channel failed to open" }));
            channel.addEventListener("open", onOpen);
            channel.addEventListener("close", onFailure);
            channel.addEventListener("error", onFailure);
        });
    }
    async #invokeOnce(request) {
        await this.connect();
        const channel = this.#channel;
        if (channel === undefined || channel.readyState !== "open") {
            throw new TransportError({ code: "offline", message: "WebRTC data channel is not open" });
        }
        const id = this.#idGenerator();
        const response = await new Promise((resolve, reject) => {
            this.#pending = { reject, resolve };
            this.#pendingRequestId = id;
            this.#enqueueFrame({
                body: request.payload,
                capability: this.#capabilityForOperation(request.operation),
                headers: request.headers ?? {},
                id,
                method: "POST",
                route: request.operation,
                type: "request",
                version: CLIENT_RPC_VERSION,
            });
        });
        return response;
    }
    #enqueueFrame(frame) {
        const encoded = encodeClientRpcFrame(frame);
        for (const chunk of segmentFrame(encoded, MAX_DATACHANNEL_CHUNK_BYTES))
            this.#outbound.push(chunk);
        this.#flushOutbound();
    }
    #flushOutbound() {
        const channel = this.#channel;
        if (channel === undefined || channel.readyState !== "open")
            return;
        while (this.#outbound.length > 0) {
            if (channel.bufferedAmount >= BUFFERED_AMOUNT_LOW_THRESHOLD)
                return;
            const chunk = this.#outbound.shift();
            if (chunk === undefined)
                return;
            channel.send(chunk);
        }
    }
    #onChannelMessage(event) {
        const data = messageData(event);
        if (data === undefined)
            return;
        let frame;
        try {
            frame = this.#inboundReassembler.push(data);
        }
        catch {
            this.#inboundReassembler.reset();
            return;
        }
        if (frame === undefined)
            return;
        let decoded;
        try {
            decoded = decodeClientRpcFrame(frame);
        }
        catch {
            return;
        }
        if (decoded.type !== "response")
            return;
        this.#resolvePending(decoded);
    }
    #resolvePending(response) {
        if (this.#pending === undefined || this.#pendingRequestId !== response.id)
            return;
        const pending = this.#pending;
        this.#pending = undefined;
        this.#pendingRequestId = undefined;
        if (response.status >= 400 || response.error !== undefined) {
            pending.reject(clientRpcResponseError(response.status, response.error, "WebRTC client-rpc request failed", response.body));
            return;
        }
        pending.resolve(response.body);
    }
    #sendCancelBestEffort(requestId) {
        try {
            this.#enqueueFrame({ id: requestId, type: "cancel", version: CLIENT_RPC_VERSION });
        }
        catch {
            // Best effort: the connection is already being torn down.
        }
    }
    #onLocalCandidate(event) {
        const candidate = iceCandidateFrom(event);
        if (candidate === undefined || this.#offerNonce === undefined)
            return;
        void this.#signaling
            .sendCandidate({
            candidate: candidate.candidate,
            offer_nonce: this.#offerNonce,
            ...(candidate.sdpMid === undefined ? {} : { sdp_mid: candidate.sdpMid }),
            ...(candidate.sdpMLineIndex === undefined ? {} : { sdp_mline_index: candidate.sdpMLineIndex }),
        })
            .catch(() => undefined);
    }
    #onIceConnectionStateChange() {
        const peerConnection = this.#peerConnection;
        if (peerConnection === undefined || this.#closed)
            return;
        // AC: on ICE failure / candidate-pair loss, fall back immediately to
        // the baseline transport rather than attempting an automatic retry —
        // `restartIce()` remains available as an explicit, caller-driven
        // recovery primitive. Publishing "offline" here is what lets
        // `ManagedHomeNodeClient` retire this client and fall back to WAN
        // HTTPS/WSS without a document reload.
        if (peerConnection.iceConnectionState === "failed" || peerConnection.iceConnectionState === "disconnected") {
            this.#setState({ changedAt: this.#now(), path: "offline", status: "offline" });
        }
    }
    #onChannelClosed() {
        if (this.#closed)
            return;
        this.#setState({ changedAt: this.#now(), path: "offline", status: "offline" });
    }
    #setState(next) {
        if (this.state.status === next.status && this.state.path === next.path)
            return;
        this.state = next;
        for (const listener of [...this.#listeners])
            listener({ state: next, type: "transport.state-changed" });
    }
}
function defaultPeerConnectionFactory(config) {
    const rtcConfig = config.iceServers === undefined ? {} : { iceServers: config.iceServers };
    return new RTCPeerConnection(rtcConfig);
}
function defaultOfferNonceGenerator() {
    const bytes = new Uint8Array(24);
    if (typeof globalThis.crypto?.getRandomValues === "function")
        globalThis.crypto.getRandomValues(bytes);
    else
        for (let index = 0; index < bytes.length; index += 1)
            bytes[index] = Math.floor(Math.random() * 256);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
function extractDtlsFingerprint(sdp) {
    for (const line of sdp.split(/\r\n|\n/)) {
        const trimmed = line.trim();
        if (trimmed.startsWith("a=fingerprint:"))
            return trimmed.slice("a=fingerprint:".length).trim();
    }
    return undefined;
}
function messageData(event) {
    if (typeof event !== "object" || event === null)
        return undefined;
    const data = event.data;
    if (data instanceof Uint8Array)
        return data;
    if (data instanceof ArrayBuffer)
        return new Uint8Array(data);
    return undefined;
}
function iceCandidateFrom(event) {
    if (typeof event !== "object" || event === null)
        return undefined;
    const candidate = event.candidate;
    if (typeof candidate !== "object" || candidate === null)
        return undefined;
    const record = candidate;
    if (typeof record.candidate !== "string" || record.candidate.length === 0)
        return undefined;
    return {
        candidate: record.candidate,
        ...(typeof record.sdpMLineIndex === "number" ? { sdpMLineIndex: record.sdpMLineIndex } : {}),
        ...(typeof record.sdpMid === "string" ? { sdpMid: record.sdpMid } : {}),
    };
}
function closedError() {
    return new TransportError({ code: "offline", message: "WebRTC home-node client is closed" });
}
//# sourceMappingURL=webrtc-home-node-client.js.map